import store from 'store'
import { notifyExtensionClipboardCopy } from 'utils/extensionBridge'

// OPD-22: 클립보드 자동삭제 기능 보류(운영 비활성화).
// 브라우저 보안상 다른 앱 사용 중(브라우저 비포커스)엔 OS 클립보드를 비울 수 없어
// (Native Messaging 필요) "어떤 상황에서도 N분 후 삭제" 요구를 충족할 수 없다 → UI 숨김 + 동작 차단.
// 재활성화 시 이 플래그와 익스텐션 constants.ts 의 CLIPBOARD_AUTO_CLEAR_ENABLED 를 함께 true 로.
export const CLIPBOARD_AUTO_CLEAR_ENABLED = false

/**
 * 웹앱 클립보드 자동삭제 (best-effort, 권한0).
 *
 * 복사 후 설정한 유지시간(localStorage app.settings.clipboardClearMs) 경과 시 클립보드를 빈 값으로 덮어쓴다.
 * navigator.clipboard.writeText 는 document 포커스가 있어야 동작하므로 "포커스/복귀 시에만" 삭제된다
 * (다른 앱에 머무는 동안은 못 지우고, 브라우저로 돌아오는 순간 flush). 익스텐션이 설치돼 있으면
 * 익스텐션이 별도 경로로 더 신뢰성 있게 처리한다 — 본 모듈은 미설치 환경의 폴백.
 *
 * - wall-clock(copiedAt 대비 현재 시각) 기준 — 백그라운드 탭 setTimeout throttling 대응.
 * - 삭제 시점에 유지시간을 redux 에서 다시 읽음 — 타이머 가동 중 설정을 바꿔도 즉시 반영.
 * - 새 복사 시 타이머 리셋. clipboardClearMs=0(Never) 이면 예약하지 않음.
 */

let pendingCopiedAt = null
let timerId = null
let listenersBound = false

const getDurationMs = () => {
  // 삭제 시점 최신값을 localStorage 에서 재조회(가동 중 설정 변경 즉시 반영).
  // 의존성: settings/CHANGE_SETTING saga 가 clipboardClearMs 를 `app.settings.*` 로 localStorage persist
  // (redux/settings/sagas.js). Redux-only 상태로 오해해 localStorage 쓰기를 누락하면 이 값이 stale 해진다.
  const ms = store.get('app.settings.clipboardClearMs')
  return typeof ms === 'number' ? ms : 0
}

const writeEmpty = () => {
  // navigator.clipboard 는 비보안(http) 컨텍스트/일부 환경에서 undefined → 접근 자체가 throw.
  // 포커스 없으면 writeText 는 NotAllowedError → 대기 유지, 복귀 시 재시도.
  if (!navigator.clipboard?.writeText) return
  navigator.clipboard
    .writeText('')
    .then(() => {
      pendingCopiedAt = null
    })
    .catch((e) => {
      // NotAllowedError(포커스 없음)는 대기 유지 → 복귀 시 재시도.
      // 그 외(영구 실패: 권한 정책 등)는 focus 마다 무한 재시도하지 않도록 대기 정리.
      if (e?.name !== 'NotAllowedError') pendingCopiedAt = null
    })
}

const tryFlush = () => {
  if (pendingCopiedAt == null) return
  const durationMs = getDurationMs()
  if (durationMs <= 0) {
    // 설정이 Never 로 바뀜 → 대기 취소.
    pendingCopiedAt = null
    return
  }
  if (Date.now() - pendingCopiedAt >= durationMs) {
    writeEmpty()
  }
}

const bindListeners = () => {
  if (listenersBound) return
  listenersBound = true
  // 미포커스 중 못 지운 건을 브라우저 복귀(포커스/가시성 전환) 시 wall-clock 만료면 flush.
  window.addEventListener('focus', tryFlush)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tryFlush()
  })
}

/**
 * 복사 직후 호출. 유지시간 경과 후 클립보드를 비우도록 예약한다. (Never 면 no-op)
 */
export const scheduleClipboardClear = () => {
  const durationMs = getDurationMs()
  if (timerId) {
    clearTimeout(timerId)
    timerId = null
  }
  if (durationMs <= 0) {
    pendingCopiedAt = null
    return
  }
  bindListeners()
  pendingCopiedAt = Date.now()
  // 익스텐션이 설치돼 있으면 background 알람으로 "포커스된 아무 탭"에서 비우도록 보강한다
  // (웹 setTimeout 은 우리 탭 포커스일 때만 가능). 옵션은 정수 분이라 durationMs/60000 은 정수.
  notifyExtensionClipboardCopy(String(durationMs / 60000))
  timerId = setTimeout(() => {
    timerId = null
    tryFlush()
  }, durationMs)
}

/**
 * 즉시 클리어(유지시간 무관). 로그아웃 시 호출. 대기 중인 safebox 복사가 있을 때만 비운다
 * (무관한 클립보드 내용 오삭제 방지 — 우리가 예약한 게 없으면 건드리지 않음). 포커스 필요.
 */
// 항상 Promise 를 반환해 호출측(로그아웃)이 await 로 클리어 완료를 보장하게 한다(반환 타입 일관).
export const clearClipboardNow = () => {
  if (timerId) {
    clearTimeout(timerId)
    timerId = null
  }
  if (pendingCopiedAt == null) return Promise.resolve()
  pendingCopiedAt = null
  if (!navigator.clipboard?.writeText) return Promise.resolve()
  return navigator.clipboard.writeText('').catch(() => {})
}
